import type { VisualizationType } from '@tempo-io/roadmap-dto';

const LOOP_VIEW_VISUALIZATION_KEY = 'nexus_loop_view_last_visited_visualization';
const LOOP_MAIN_VISUALIZATION_KEY = 'nexus_loop_main_last_visited_visualization';
const LOOP_VIEW_KEY = 'nexus_loop_last_visited_view';

export type LastVisitedTarget = { type: 'view'; id: number } | { type: 'main' };

type VisualizationStorage = Partial<Record<number, VisualizationType>>;
type ViewStorage = Partial<Record<number, LastVisitedTarget>>;

function read<T>(key: string): T extends object ? T : never {
  try {
    const raw = window.localStorage.getItem(key);
    return (raw ? JSON.parse(raw) : {}) as T extends object ? T : never;
  } catch {
    return {} as T extends object ? T : never;
  }
}

function write(key: string, data: object): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(data));
  } catch {
    // localStorage disabled or quota exceeded — silently ignore
  }
}

export function getLastVisitedVisualization(viewId: number): VisualizationType | undefined {
  if (typeof window === 'undefined') return undefined;
  return read<VisualizationStorage>(LOOP_VIEW_VISUALIZATION_KEY)[viewId];
}

export function saveLastVisitedVisualization(viewId: number, type: VisualizationType): void {
  if (typeof window === 'undefined') return;
  write(LOOP_VIEW_VISUALIZATION_KEY, {
    ...read<VisualizationStorage>(LOOP_VIEW_VISUALIZATION_KEY),
    [viewId]: type,
  });
}

export function getLastVisitedMainVisualization(loopId: number): VisualizationType | undefined {
  if (typeof window === 'undefined') return undefined;
  return read<VisualizationStorage>(LOOP_MAIN_VISUALIZATION_KEY)[loopId];
}

export function saveLastVisitedMainVisualization(loopId: number, type: VisualizationType): void {
  if (typeof window === 'undefined') return;
  write(LOOP_MAIN_VISUALIZATION_KEY, {
    ...read<VisualizationStorage>(LOOP_MAIN_VISUALIZATION_KEY),
    [loopId]: type,
  });
}

export function getLastVisitedView(loopId: number): LastVisitedTarget | undefined {
  if (typeof window === 'undefined') return undefined;
  return read<ViewStorage>(LOOP_VIEW_KEY)[loopId];
}

export function saveLastVisitedView(loopId: number, target: LastVisitedTarget): void {
  if (typeof window === 'undefined') return;
  write(LOOP_VIEW_KEY, { ...read<ViewStorage>(LOOP_VIEW_KEY), [loopId]: target });
}
